import type { MouseEvent as MouseEvent_ } from 'react';
import { ExternalLink } from 'lucide-react';
import { ENTITY_STATE_LABELS } from '../../lib/labels';
import { FOCUS_RING_TIGHT, HIT_TARGET } from './focus';

/**
 * The three states a customer or product can be in on a ticket (P30, Roman's
 * decision 6). `identified` is the plain case and stays plain — a badge on
 * every row would be noise.
 */
export type EntityState = 'identified' | 'notIdentified' | 'toConfirm';

/**
 * Name-first display for a screen that asks for a decision — a code alone
 * ("dit-100332") tells nobody whether the customer is real, so the resolved
 * name leads and the code goes small/parenthesized/hover (P11).
 *
 * `raw` is the unresolved text the ticket captured (RawCustomerText/
 * RawProductText) — the fallback when neither a name nor a code resolved.
 *
 * =========================================================================
 * WHY `state` EXISTS (P30)
 * =========================================================================
 * Until P30 the three cases rendered identically: one grey line, whether it
 * held a resolved ERP name or the words somebody typed in a mail. So the only
 * cue that a ticket had no customer at all was the ABSENCE of a code and a
 * HubSpot chip — a thing that is not there, on a board of 47 rows. Live on
 * 2026-09-02 that was 13 open tickets with no customer and 5 with no article.
 *
 * `notIdentified` therefore says so in words, in an outlined red chip, and
 * shows the mail's own text in italics so it reads as a quotation rather than
 * as a record. `toConfirm` is amber: an id IS set, but the ticket still carries
 * an open question about it. Neither is an error state — an unknown sender is
 * legitimate (Bruno D16) — they are unfinished work, which is what the board
 * is for.
 */
export function NameWithCode({
  name, code, raw, state = 'identified', compact = false,
}: {
  name: string | null;
  code: string | null;
  raw?: string | null;
  state?: EntityState;
  /**
   * `compact` (P37) keeps the code out of the CELL and in the tooltip. On a
   * board row "(100755)" is ~55 px of a 143 px customer column spent on the
   * thing the house rule already calls secondary — "show names, not codes; the
   * id goes small or on hover". Measured on the live board: with the code
   * drawn, 35 of 41 customer cells were ellipsised. The ticket screen, which
   * has room and is where the id gets used, passes nothing and keeps it.
   */
  compact?: boolean;
}) {
  const display = name ?? raw ?? code;

  if (state === 'notIdentified') {
    return (
      // P37: no `flex-wrap`, and `min-w-0` on the text. A flex container wraps
      // its items whatever `white-space` says, so inside a table cell this was
      // one of the things that made a row 79 or 100 px tall. The cell clips; the
      // tooltip and the ticket screen carry the full text.
      <span className="inline-flex max-w-full items-baseline gap-1 whitespace-nowrap" title={`${ENTITY_STATE_LABELS.notIdentified.tooltip}${display ? `\n\n${display}` : ''}`}>
        {/*
          * P38, task 6: this was `text-[10px] uppercase tracking-wide` and the
          * audit counted it 15 times on one screen. At 12 px, sentence case and
          * no tracking, the chip is NARROWER than it was — uppercase letters are
          * wider than lowercase ones and `tracking-wide` adds .025em per
          * character — so the floor was reached by spending nothing. Measured:
          * "Not identified" 76 px before, 79 px after.
          *
          * `border-err-edge` / `text-err` rather than `red-400` / `red-600`:
          * §2's tokens, the same red the draft warnings use, and 4.5:1 rather
          * than 3.99:1.
          */}
        <span className="shrink-0 rounded-ctl border border-err-edge px-1 py-px text-xs font-medium text-err">
          {ENTITY_STATE_LABELS.notIdentified.label}
        </span>
        {display
          ? <span className="min-w-0 truncate italic text-muted">“{display}”</span>
          : <span className="italic text-muted">the mail named none</span>}
      </span>
    );
  }

  if (!display) return <>—</>;

  return (
    <span className="inline-flex max-w-full items-baseline gap-1 whitespace-nowrap" title={code && name ? `${display} (${code})` : (display ?? undefined)}>
      <span className="min-w-0 truncate">
        {display}
        {code && name && !compact && (
          <span className="ml-1 text-xs text-muted" title={code}>
            ({code})
          </span>
        )}
      </span>
      {state === 'toConfirm' && (
        <span
          title={ENTITY_STATE_LABELS.toConfirm.tooltip}
          // Amber, and it stays amber: §8 assigns this state the tint by name and
          // `p30:verify` asserts it. It is a fact somebody must confirm, which is
          // what amber means — not a queue it sits in.
          className="shrink-0 rounded-ctl border border-warn-edge px-1 py-px text-xs font-medium text-warn-ink"
        >
          {ENTITY_STATE_LABELS.toConfirm.label}
        </span>
      )}
    </span>
  );
}

/**
 * A link to another system. `stopPropagation` matters everywhere this sits
 * inside a clickable table row (TicketBoard navigates on row click).
 * No mapping/config -> render nothing, never an error (P11 display rule).
 *
 * =========================================================================
 * `compact` (P37) — WHY THE WORD LEAVES THE ROW
 * =========================================================================
 * P36 measured 31 of these inside table rows at **61 × 16 px**: below the
 * 32 px hit-target floor in both dimensions, and 61 px of a customer column
 * that has ~150 px for a company name. It left them deliberately, because
 * making them taller is a row-height decision — which is this phase.
 *
 * In `compact` the chip is the icon alone in a 32 px target, named by
 * `aria-label` and by the same `title` it always had. The word "HubSpot" is
 * what it cost, and the icon is the one external-link glyph the app uses; on a
 * screen with room for words (the ticket screen, P39) the default still writes
 * it out.
 */
function ExternalRefLink({ url, label, colorClassName, compact = false }: {
  url: string | null;
  label: string;
  colorClassName: string;
  compact?: boolean;
}) {
  if (!url) return null;
  const common = {
    href: url,
    target: '_blank',
    rel: 'noopener noreferrer',
    onClick: (e: MouseEvent_) => e.stopPropagation(),
    title: `Open in ${label}`,
  };
  if (compact) {
    return (
      <a
        {...common}
        aria-label={`Open in ${label}`}
        className={`inline-flex shrink-0 items-center justify-center rounded-ctl ${HIT_TARGET} ${colorClassName} ${FOCUS_RING_TIGHT}`}
      >
        <ExternalLink className="h-4 w-4" />
      </a>
    );
  }
  return (
    <a {...common} className={`inline-flex items-center gap-0.5 text-xs hover:underline ${colorClassName} ${FOCUS_RING_TIGHT}`}>
      <ExternalLink className="h-3 w-3" />
      {label}
    </a>
  );
}

export function HubspotChip({ url, compact = false }: { url: string | null; compact?: boolean }) {
  return <ExternalRefLink url={url} label="HubSpot" colorClassName="text-hubspot hover:text-hubspot-700" compact={compact} />;
}

export function BcLink({ url }: { url: string | null }) {
  return <ExternalRefLink url={url} label="Business Central" colorClassName="text-info" />;
}
